/**
 * errorReporter.ts  -  Sends browser crashes and failed API calls to the
 * server log (/api/v1/client-errors), so a teacher saying "it didn't work"
 * leaves a trace we can read. No form data or personal details are sent.
 */

const ENDPOINT = `${import.meta.env.VITE_API_BASE_URL ?? "/api/v1"}/client-errors`;
const seen = new Set<string>();

interface Report {
  kind: "crash" | "promise" | "api";
  message: string;
  stack?: string;
  status?: number;
  api?: string;
}

export function reportError(r: Report) {
  const key = `${r.kind}|${r.message}|${r.api ?? ""}`;
  if (seen.has(key) || seen.size > 50) return;
  seen.add(key);
  const body = JSON.stringify({ ...r, page: window.location.pathname });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([body], { type: "application/json" }),
      );
    } else {
      void fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
    }
  } catch {
    /* reporting must never break the page */
  }
}

export function installErrorReporter() {
  window.addEventListener("error", (e) => {
    reportError({
      kind: "crash",
      message: e.message || "Script error",
      stack: e.error instanceof Error ? e.error.stack : undefined,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const err = e.reason;
    // Failed API calls are reported by the API client with more detail.
    if (err && typeof err === "object" && "isAxiosError" in err) return;
    reportError({
      kind: "promise",
      message: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
    });
  });
}
